import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { toast } from "sonner";
import { Sparkles, Download, ScrollText, FileDown } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AI_MODELS, AI_MODEL_LABELS } from "@/lib/data";
import RevisionPanel from "@/components/RevisionPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DocumentPanel({ tender, canManage, onGenerated }) {
  const [streaming, setStreaming] = useState(false);
  const [streamText, setStreamText] = useState("");
  const [model, setModel] = useState(tender.document_model || "gpt-5.4");

  const doc = streaming ? streamText : tender.document;

  const generate = async (revise = false) => {
    setStreaming(true);
    setStreamText("");
    try {
      const res = await fetch(`${API}/tenders/${tender.id}/generate-document?model=${model}&revise=${revise}`, { method: "POST", credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) {
            acc += payload.delta;
            setStreamText(acc);
          } else if (payload.error) {
            throw new Error(payload.error);
          }
        }
      }
      toast.success("Compliant tender document generated.");
      onGenerated();
    } catch (e) {
      toast.error("Stream interrupted — recovering saved draft.");
      onGenerated();
    } finally {
      setStreaming(false);
    }
  };

  const download = () => {
    const blob = new Blob([tender.document], { type: "text/markdown" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${tender.reference_number.replaceAll("/", "-")}.md`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  if (!doc && !streaming) {
    return (
      <div className="bg-white border border-slate-200 p-16 text-center" data-testid="document-empty-state">
        <ScrollText className="w-10 h-10 text-slate-300 mx-auto mb-5" aria-hidden="true" />
        <h3 className="font-serif text-2xl text-slate-900 mb-3">No document drafted yet</h3>
        <p className="text-sm text-slate-500 max-w-lg mx-auto leading-relaxed mb-8">
          Generate a complete, legally structured {tender.tender_type} document drafted in compliance with the
          procurement laws of <strong>{tender.country}</strong> — falling back to UNCITRAL Model Law and the UN
          Procurement Manual where local rules are silent.
        </p>
        {canManage && (
          <div className="flex items-center justify-center gap-3">
            <Select value={model} onValueChange={setModel}>
              <SelectTrigger data-testid="doc-model-select" className="w-[200px] bg-white border-slate-300">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <button
              onClick={() => generate(false)}
              data-testid="generate-document-btn"
              className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-8 py-3.5 text-sm font-semibold hover:bg-[#003F7A] transition-colors duration-200"
            >
              <Sparkles className="w-4 h-4" aria-hidden="true" /> Generate Compliant Document
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-200">
      <div className="flex items-center justify-between px-8 py-4 border-b border-slate-200 bg-slate-50">
        <div className="flex items-center gap-2 text-sm text-slate-600">
          <ScrollText className="w-4 h-4 text-[#0050A0]" aria-hidden="true" />
          {streaming ? (
            <span data-testid="document-streaming-indicator" className="text-[#0050A0] font-medium">Drafting live with {AI_MODEL_LABELS[model]} — applying {tender.country} procurement framework…</span>
          ) : (
            <span>
              Legally drafted {tender.tender_type} · {tender.country} compliance
              <span className="font-semibold text-[#0050A0]"> · Draft v{tender.document_version || 1}</span>
              {tender.document_model && <span className="text-slate-400"> · {AI_MODEL_LABELS[tender.document_model] || tender.document_model}</span>}
            </span>
          )}
        </div>
        {!streaming && (
          <div className="flex gap-3 items-center">
            {canManage && (
              <>
                <Select value={model} onValueChange={setModel}>
                  <SelectTrigger data-testid="doc-model-select-toolbar" className="w-[180px] h-8 text-xs bg-white border-slate-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {AI_MODELS.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button onClick={() => generate(false)} data-testid="regenerate-document-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-4 py-2 text-xs font-semibold hover:bg-slate-100 transition-colors duration-200">
                  <Sparkles className="w-3.5 h-3.5" aria-hidden="true" /> Regenerate
                </button>
              </>
            )}
            <button onClick={download} data-testid="download-document-btn" className="inline-flex items-center gap-2 border border-slate-300 text-slate-600 px-4 py-2 text-xs font-semibold hover:bg-slate-100 transition-colors duration-200">
              <Download className="w-3.5 h-3.5" aria-hidden="true" /> Markdown
            </button>
            <a href={`${API}/tenders/${tender.id}/document/pdf`} data-testid="download-pdf-btn" className="inline-flex items-center gap-2 bg-[#0050A0] text-white px-4 py-2 text-xs font-semibold hover:bg-[#003F7A] transition-colors duration-200">
              <FileDown className="w-3.5 h-3.5" aria-hidden="true" /> Download PDF
            </a>
          </div>
        )}
      </div>
      <div className={`doc-prose p-10 max-w-4xl ${streaming ? "doc-cursor" : ""}`} data-testid="document-content">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{doc || ""}</ReactMarkdown>
      </div>
      {tender.document && <RevisionPanel tender={tender} canManage={canManage} onRevise={() => generate(true)} streaming={streaming} />}
    </div>
  );
}
